"use client";

import React, { Suspense, type FormEvent, useEffect, useState } from "react";
import { signIn } from "next-auth/react";
import { useSearchParams } from "next/navigation";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { Alert, Button, TextField } from "@ui-kit";

const localDevMode = process.env.NODE_ENV === "development";

const PageContent = () => {
  const searchParams = useSearchParams();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const callbackUrl = searchParams.get("return-to") ?? undefined;

  useEffect(() => {
    if (localDevMode) {
      return;
    }
    void signIn("google", { callbackUrl });
  }, [callbackUrl]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedEmail = email.trim();

    if (!normalizedEmail) {
      setError("Email is required");
      return;
    }

    setError(null);
    setIsSubmitting(true);

    const result = await signIn("dev-credentials", {
      email: normalizedEmail,
      callbackUrl: callbackUrl || "/",
      redirect: false,
    });

    setIsSubmitting(false);

    if (!result || result.error) {
      setError("This email is not allowed to sign in.");
      return;
    }

    const destination = callbackUrl || "/";
    window.location.assign(destination);
  }

  if (!localDevMode) {
    return <></>;
  }

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        bgcolor: "grey.100",
      }}
    >
      <Paper
        elevation={2}
        sx={{
          width: "100%",
          maxWidth: 440,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Stack component="form" spacing={2} onSubmit={handleSubmit}>
          <Box>
            <Typography variant="h5" component="h1" fontWeight={700}>
              Dev Sign In
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Local development only
            </Typography>
          </Box>

          <TextField
            id="dev-sign-in-email"
            name="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="name@jeff-app.com"
            label="Email"
            autoComplete="email"
            required
            fullWidth
            size="small"
          />

          <Button disabled={isSubmitting} type="submit" variant="contained">
            {isSubmitting ? "Signing in..." : "Dev Sign In"}
          </Button>

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </Paper>
    </Box>
  );
};

const Page = () => (
  <Suspense>
    <PageContent />
  </Suspense>
);

export default Page;
